import type {Timer} from '../@types/timeout'

export function debounce<F extends (...args: any[]) => void>(func: F, wait?: number): (...args: Parameters<F>) => void {
  let lastArgs: undefined | Parameters<F>[] = undefined
  let timer: Timer = undefined

  const invoke = () => {
    timer = undefined
    if (lastArgs === undefined) {
      return
    }

    const args = lastArgs
    lastArgs = undefined

    func(...args)

    timer = setTimeout(invoke, wait)
  }

  return (...args: Parameters<F>): void => {
    lastArgs = args
    if (timer === undefined) {
      invoke()
    }
  }
}

export function asyncDebounce<F extends (...args: any[]) => Promise<any>>(func: F, wait?: number): (...args: Parameters<F>) => ReturnType<F> {
  let lastArgs: undefined | Parameters<F>[] = undefined
  let promise: undefined | ReturnType<F> = undefined

  const debounced = debounce((resolve, reject) => {
    if (!lastArgs) {
      return
    }

    func(...lastArgs)
      .then(resolve)
      .catch(reject)
  }, wait)

  return (...args: Parameters<F>): ReturnType<F> => {
    lastArgs = args
    if (promise) {
      return promise
    }

    promise = new Promise((resolve, reject) => {
      debounced(
        (value: any) => {
          promise = undefined
          resolve(value)
        },
        (reason?: any) => {
          promise = undefined
          reject(reason)
        },
      )
    }) as ReturnType<F>

    return promise
  }
}
